const notifyDrawerProductCache = new Map();

class NotifyDrawer extends HTMLElement {
  connectedCallback() {
    this.gallery = this.querySelector("[data-notify-drawer-gallery]");
    this.mediaScroll = this.querySelector(".notify-drawer-media-scroll");
    this.dots = this.querySelector("[data-notify-drawer-dots]");
    this.content = this.querySelector(".notify-drawer-content");
    this.productContainer = this.querySelector("[data-notify-drawer-product]");
    this.form = this.querySelector("[data-notify-drawer-form]");
    this.input = this.querySelector("#notify-drawer-email");
    this.status = this.querySelector("[data-notify-drawer-status]");
    this.submit = this.querySelector("[data-notify-drawer-submit]");
    this.inStock = this.querySelector("[data-notify-drawer-in-stock]");
    this.buyButton = this.querySelector("[data-notify-drawer-buy]");

    this.handleShow = (event) => this.show(event.detail?.source);
    this.handleKeydown = (event) => {
      if (event.key === "Escape" && this.classList.contains("state--active")) {
        this.hide();
      }
    };
    this.handleClick = (event) => {
      if (event.target.closest("[data-notify-drawer-close]")) {
        this.hide();
        return;
      }

      if (event.target.closest("[data-notify-drawer-buy]")) {
        this.buy();
        return;
      }

      const option = event.target.closest("[data-notify-drawer-option]");
      if (option) this.selectOption(option);
    };
    this.handleGalleryScroll = () => {
      window.cancelAnimationFrame(this.galleryScrollFrame);
      this.galleryScrollFrame = window.requestAnimationFrame(() => this.updateDots());
    };
    this.handleSubmit = (event) => this.subscribe(event);

    document.addEventListener("notifydrawer:show", this.handleShow);
    document.addEventListener("keydown", this.handleKeydown);
    this.addEventListener("click", this.handleClick);
    this.gallery.addEventListener("scroll", this.handleGalleryScroll, { passive: true });
    this.form.addEventListener("submit", this.handleSubmit);
  }

  disconnectedCallback() {
    document.removeEventListener("notifydrawer:show", this.handleShow);
    document.removeEventListener("keydown", this.handleKeydown);
    this.removeEventListener("click", this.handleClick);
    this.gallery.removeEventListener("scroll", this.handleGalleryScroll);
    this.form.removeEventListener("submit", this.handleSubmit);
    window.clearTimeout(this.hideTimer);
    window.cancelAnimationFrame(this.galleryScrollFrame);
  }

  async show(trigger) {
    if (!trigger) return;

    const productSource = trigger.closest("product-card") || trigger.closest(".product-grid");

    window.clearTimeout(this.hideTimer);
    this.trigger = trigger;
    this.product = { ...trigger.dataset };
    this.selectedVariant = null;
    this.resetStatus();

    if (productSource) this.populateGallery(productSource);

    const requestId = (this.requestId || 0) + 1;
    this.requestId = requestId;

    const openButton = trigger.querySelector("[data-notify-open]");
    openButton?.classList.add("state--loading");

    try {
      await this.populateProduct(trigger.dataset.productUrl);
    } catch (error) {
      console.error("Notify drawer: product content failed to load", error);
      this.form.hidden = true;
      this.showStatus(this.status.dataset.error, true);
    } finally {
      openButton?.classList.remove("state--loading");
    }

    if (requestId !== this.requestId) return;

    if (this.product.variantChosen === "true") this.selectChosenVariant();

    this.setAttribute("aria-hidden", "false");
    this.classList.add("state--active");
    this.content.focus({ preventScroll: true });
  }

  hide() {
    if (!this.classList.contains("state--active")) return;

    this.classList.remove("state--active");
    this.setAttribute("aria-hidden", "true");
    this.trigger?.querySelector("[data-notify-open]")?.focus();

    window.clearTimeout(this.hideTimer);
    this.hideTimer = window.setTimeout(() => this.reset(), 280);
  }

  reset() {
    this.gallery.replaceChildren();
    this.dots.replaceChildren();
    this.productContainer.replaceChildren();
    this.form.reset();
    this.resetStatus();
    this.trigger = null;
    this.product = null;
    this.selectedVariant = null;
  }

  selectChosenVariant() {
    const option = Array.from(this.options()).find(
      (button) => button.dataset.variantId === this.product?.variantId
    );
    if (option) this.selectOption(option);
  }

  options() {
    return this.querySelectorAll("[data-notify-drawer-option]");
  }

  needsOption() {
    return this.options().length > 0 && !this.selectedVariant;
  }

  flagMissingOption() {
    const options = this.querySelector("[data-notify-drawer-options]");
    options?.classList.add("state--error");
    options?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }

  selectOption(option) {
    this.querySelector("[data-notify-drawer-options]")?.classList.remove("state--error");
    this.options().forEach((button) => {
      const isSelected = button === option;
      button.setAttribute("aria-pressed", String(isSelected));
      button.classList.toggle("selected", isSelected);
    });

    this.selectedVariant = {
      id: option.dataset.variantId,
      title: option.dataset.variantTitle,
      available: option.dataset.variantAvailable === "true"
    };

    this.showBuyOption(this.canBuySelected());
  }

  canBuySelected() {
    return Theme.NotifySignup.canBuy(this.selectedVariant, this.product?.notifyMode);
  }

  showBuyOption(isVisible) {
    this.resetStatus();
    this.inStock.hidden = !isVisible;
    this.form.hidden = isVisible;
  }

  lineProperties() {
    const form = this.trigger?.closest("form");
    if (form) {
      return Array.from(form.querySelectorAll('input[type="hidden"][name^="properties["]')).map(
        (input) => [input.name, input.value]
      );
    }

    const properties = [["properties[_web]", "true"]];
    if (this.querySelector("[data-notify-drawer-options][data-final-sale]")) {
      properties.push(["properties[FINAL SALE]", "No Returns"]);
    }
    return properties;
  }

  async buy() {
    if (!this.canBuySelected()) return;

    const data = new FormData();
    data.set("id", this.selectedVariant.id);
    data.set("quantity", "1");
    this.lineProperties().forEach(([name, value]) => data.set(name, value));

    this.buyButton.classList.add("state--loading");
    this.buyButton.disabled = true;

    try {
      const response = await fetch("/cart/add.js", { method: "POST", body: data });
      if (!response.ok) throw new Error(`Add to cart failed: ${response.status}`);

      const item = await response.json();
      this.hide();
      document.dispatchEvent(new CustomEvent("addtocart", { detail: item }));
    } catch (error) {
      console.error("Notify drawer: add to cart failed", error);
      this.showStatus(this.status.dataset.error, true);
    } finally {
      this.buyButton.classList.remove("state--loading");
      this.buyButton.disabled = false;
    }
  }

  resetStatus() {
    this.status.hidden = true;
    this.status.textContent = "";
    this.status.classList.remove("state--error");
    this.form.hidden = false;
    this.inStock.hidden = true;
  }

  populateGallery(productSource) {
    this.gallery.replaceChildren();
    const seenSources = new Set();

    productSource
      .querySelectorAll("[data-product-image] img, [data-product-hover] img, .product-gallery-slide img")
      .forEach((image) => {
        const source = image.currentSrc || image.src;
        if (!source || seenSources.has(source)) return;

        seenSources.add(source);
        const item = document.createElement("div");
        item.className = "notify-drawer-gallery-item";

        const clone = image.cloneNode(true);
        clone.removeAttribute("id");
        clone.loading = "eager";
        clone.decoding = "async";
        item.appendChild(clone);
        this.gallery.appendChild(item);
      });

    this.gallery.scrollLeft = 0;
    this.mediaScroll?.refresh();
    this.buildDots();
  }

  async populateProduct(productUrl) {
    if (!productUrl) throw new Error("Missing product URL");

    const url = new URL(productUrl, window.location.origin);
    url.searchParams.set("section_id", "notify-drawer");

    let html = notifyDrawerProductCache.get(url.href);
    if (!html) {
      const response = await fetch(url.href, {
        headers: { "X-Requested-With": "XMLHttpRequest" }
      });
      if (!response.ok) throw new Error(`Fetch failed: ${response.status}`);
      html = await response.text();
      notifyDrawerProductCache.set(url.href, html);
    }

    const documentFragment = new DOMParser().parseFromString(html, "text/html");
    const productContent = documentFragment.querySelector("[data-notify-drawer-product]");
    if (!productContent) throw new Error("Missing notify drawer product content");

    this.productContainer.replaceChildren(
      ...Array.from(productContent.childNodes).map((node) => node.cloneNode(true))
    );
  }

  buildDots() {
    this.dots.replaceChildren();

    this.gallery.querySelectorAll(".notify-drawer-gallery-item").forEach((_, index) => {
      const dot = document.createElement("span");
      dot.className = "notify-drawer-dot";
      if (index === 0) dot.classList.add("is-selected");
      this.dots.appendChild(dot);
    });
  }

  updateDots() {
    const dots = Array.from(this.dots.children);
    if (!dots.length) return;

    const maxScroll = this.gallery.scrollWidth - this.gallery.clientWidth;
    const progress = maxScroll > 0 ? this.gallery.scrollLeft / maxScroll : 0;
    const activeIndex = Math.round(progress * (dots.length - 1));

    dots.forEach((dot, index) => dot.classList.toggle("is-selected", index === activeIndex));
  }

  async subscribe(event) {
    event.preventDefault();

    if (this.needsOption()) {
      this.flagMissingOption();
      return;
    }

    const email = this.input.value.trim();
    if (!email || !this.input.checkValidity()) {
      this.input.focus();
      return;
    }

    this.submit.classList.add("state--loading");
    this.submit.disabled = true;

    try {
      await Theme.NotifySignup.subscribe({
        email,
        mode: this.product?.notifyMode,
        product: {
          handle: this.product?.productHandle,
          id: this.product?.productId,
          title: this.product?.productTitle,
          url: this.product?.productUrl
        },
        variant: {
          id: this.selectedVariant?.id || this.product?.variantId,
          title: this.selectedVariant?.title || this.product?.variantTitle
        }
      });

      this.form.hidden = true;
      this.showStatus(this.status.dataset.success, false);
    } catch (error) {
      console.error("Notify drawer: signup failed", error);
      this.showStatus(this.status.dataset.error, true);
    } finally {
      this.submit.classList.remove("state--loading");
      this.submit.disabled = false;
    }
  }

  showStatus(message, isError) {
    this.status.textContent = message;
    this.status.classList.toggle("state--error", isError);
    this.status.hidden = false;
  }
}

if (!customElements.get("notify-drawer")) {
  customElements.define("notify-drawer", NotifyDrawer);
}

class NotifyTrigger extends HTMLElement {
  connectedCallback() {
    this.handleClick = (event) => {
      event.preventDefault();
      event.stopPropagation();
      document.dispatchEvent(new CustomEvent("notifydrawer:show", { detail: { source: this } }));
    };

    this.addEventListener("click", this.handleClick);
  }

  disconnectedCallback() {
    this.removeEventListener("click", this.handleClick);
  }
}

if (!customElements.get("notify-trigger")) {
  customElements.define("notify-trigger", NotifyTrigger);
}
